/**
 * Reading and writing browser storage without assuming it exists.
 *
 * `window.localStorage` is not a plain object you can poke at safely: in a
 * browser configured to block site data — Safari's "Block all cookies", the
 * equivalent Chrome setting, some embedded webviews — merely READING the
 * property throws `SecurityError`. Not calling `getItem`. Reading the
 * property.
 *
 * That distinction matters more than it sounds, because it defeats the
 * obvious guard. This is the shape the code had:
 *
 *     function safeGet(storage, key) {
 *       try { return storage.getItem(key); } catch { return null; }
 *     }
 *     safeGet(localStorage, "some-key");
 *
 * The argument is evaluated at the CALL SITE, before the function is entered,
 * so the throw happens outside the very try/catch written to contain it. The
 * helper looks defensive and catches nothing. Sentry recorded it as
 * "Failed to read the 'localStorage' property from 'Window'".
 *
 * Here the storage object is resolved INSIDE the try, so every access — the
 * property lookup included — is covered. A browser with storage switched off
 * simply behaves as though nothing was ever stored, which for everything we
 * keep in it (a dismissed popup, a remembered column layout) is the correct
 * outcome rather than a broken page.
 */

/** The storage object, or null when this browser will not hand it over. */
function resolve(kind) {
  try {
    const store = kind === "session" ? window.sessionStorage : window.localStorage;
    // Blocked storage sometimes yields null rather than throwing.
    return store || null;
  } catch {
    return null;
  }
}

export function readItem(kind, key) {
  try {
    const store = resolve(kind);
    return store ? store.getItem(key) : null;
  } catch {
    return null;
  }
}

export function writeItem(kind, key, value) {
  try {
    const store = resolve(kind);
    if (!store) return false;
    store.setItem(key, value);
    return true;
  } catch {
    // Also the quota path: a full storage throws here, and a popup failing to
    // remember it was dismissed is not a reason to break the page under it.
    return false;
  }
}

export function removeItem(kind, key) {
  try {
    const store = resolve(kind);
    if (!store) return false;
    store.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/** `readItem` + `JSON.parse`, returning `fallback` for anything unreadable. */
export function readJSON(kind, key, fallback = null) {
  const raw = readItem(kind, key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

export function writeJSON(kind, key, value) {
  try {
    return writeItem(kind, key, JSON.stringify(value));
  } catch {
    // A value with a circular reference never reaches storage at all.
    return false;
  }
}
